// TextAliveController.js — wraps the TextAlive Player and normalizes its
// callbacks into clean game events. The rest of the app never sees TextAlive types.

import { Player } from "textalive-app-api";
import { Emitter } from "./GameState.js";
import { TEXTALIVE_TOKEN, DEFAULT_SONG } from "../config.js";

export class TextAliveController {
    /**
     * @param {object} engine  GameEngine instance
     * @param {HTMLElement} mediaEl  container for the TextAlive media banner
     */
    constructor(engine, mediaEl) {
        this._engine = engine;
        this._mediaEl = mediaEl;
        this._lines = [];

        // Interpolation anchors: TextAlive's onTimeUpdate is throttled, so we
        // remember the last reported position + wall-clock time and interpolate
        // between updates for a smooth, per-frame song position.
        this._lastPos = -1;
        this._lastAt = 0;

        // Per-frame broadcast channel. The lyric/game state is updated via
        // engine.syncTime(); other tempo-reactive components subscribe here.
        this.frames = new Emitter();

        this._player = new Player({
            app: { token: TEXTALIVE_TOKEN },
            mediaElement: mediaEl,
            // Embed the (required) attribution banner directly under the media
            // player so they read as one stacked widget, not two side-by-side.
            mediaBannerPosition: "embed",
        });

        this._player.addListener({
            onAppReady: (app) => this._onAppReady(app),
            onVideoReady: (video) => this._onVideoReady(video),
            onTimeUpdate: (pos) => this._onTimeUpdate(pos),
            onStop: () => this._engine.songEnded(),
        });

        this._loop = this._loop.bind(this);
        this._raf = requestAnimationFrame(this._loop);
    }

    get player() {
        return this._player;
    }

    // Subscribe to the per-frame clock: fn({ position, isPlaying }). Returns an
    // unsubscribe function. This is the hook for future tempo-synced visuals.
    onFrame(fn) {
        return this.frames.subscribe(fn);
    }

    togglePlay() {
        if (this._player.isPlaying) this._player.requestPause();
        else this._player.requestPlay();
    }

    // Tear down the player, stop the clock, and remove injected media DOM.
    // Called on HMR dispose so dev reloads don't stack duplicate widgets/loops.
    dispose() {
        cancelAnimationFrame(this._raf);
        try {
            this._player.dispose();
        } catch (e) {
            /* ignore — player may not be fully initialized */
        }
        if (this._mediaEl) this._mediaEl.replaceChildren();
    }

    // Smoothly interpolated song position (ms), or -1 before playback starts.
    _position() {
        if (this._lastPos < 0) return -1;
        if (this._player.isPlaying) return Date.now() - this._lastAt + this._lastPos;
        return this._lastPos;
    }

    _loop() {
        const pos = this._position();
        if (pos >= 0) {
            this._engine.syncTime(pos);
            this.frames.emit({
                position: pos,
                isPlaying: this._player.isPlaying,
                beat: this._beatAt(pos),
            });
        }
        this._raf = requestAnimationFrame(this._loop);
    }

    // Beat info for tempo-reactive visuals, or null before the song is ready.
    // { progress: 0..1 within the beat, position: index in bar, length: beats/bar }
    _beatAt(position) {
        try {
            const b = this._player.findBeat(position);
            if (!b) return null;
            return { progress: b.progress(position), position: b.position, length: b.length };
        } catch (e) {
            return null;
        }
    }

    _onAppReady(app) {
        if (!app.songUrl) {
            this._player.createFromSongUrl(DEFAULT_SONG.url, { video: DEFAULT_SONG.video });
        }
    }

    // Build a normalized list of lines: one per phrase, split into characters.
    _onVideoReady(video) {
        const lines = [];
        let phrase = video.firstPhrase;

        while (phrase) {
            // Keep per-character timing so the engine can color each char as
            // it is sung.
            const chars = [];
            let c = phrase.firstChar;
            while (c && c.startTime < phrase.endTime) {
                chars.push({ text: c.text, startTime: c.startTime, endTime: c.endTime });
                c = c.next;
            }
            if (chars.length) {
                lines.push({
                    text: phrase.text,
                    startTime: phrase.startTime,
                    endTime: phrase.endTime,
                    chars,
                });
            }
            phrase = phrase.next;
        }

        this._lines = lines;
        this._engine.loadLines(lines);
    }

    // Just refresh the interpolation anchors; the rAF loop drives sync.
    _onTimeUpdate(position) {
        this._lastPos = position;
        this._lastAt = Date.now();
    }
}
